import { Bot, Puzzle } from 'lucide-react';
import { canViewGuestPii, useAuth } from '@/shared/auth';
import type {
  BookingResponse,
  ConversationDetail,
  GuestProfileResponse,
  PropertyResponse,
} from '@/shared/api';
import { ConversationAutomation } from '../automation/ConversationAutomation';
import { ConversationCases } from '../cases/ConversationCases';
import { threadDisplayName } from '../threadDisplayName';
import { ApartmentContext } from './ApartmentContext';
import { GuestContextCard } from './GuestContextCard';
import { NoteCard } from './NoteCard';
import { PanelCard } from './PanelCard';
import { BillingContextCard, StayContextCard } from './StayBillingCards';
import { useThreadContext } from './useThreadContext';

export interface ContextData {
  booking: BookingResponse | null;
  property: PropertyResponse | null;
  guest: GuestProfileResponse | null;
}

/**
 * Kontextspalte einer Unterhaltung: Aufenthalt offen, alles Weitere eingeklappt. Gastdaten nach dem
 * Server-Recht „Gastdaten sehen“, nicht nach der Rolle. `demoData` (nur ?demo=1) liefert fertige
 * Daten ohne Abruf für die Vorschau.
 */
export function ThreadContextPanel({
  thread,
  demoData,
}: {
  thread: ConversationDetail;
  demoData?: ContextData;
}) {
  const { user } = useAuth();
  const canViewPii = canViewGuestPii(user);
  const live = useThreadContext({
    threadId: thread.id,
    bookingId: demoData ? null : thread.bookingId,
    propertyId: demoData ? null : thread.propertyId,
    otaReservationId: demoData ? null : thread.otaReservationId,
    canViewPii,
  });
  const b = demoData ? demoData.booking : (live.booking.data ?? null);
  const property = demoData ? demoData.property : (live.property.data ?? null);
  const guest = demoData ? demoData.guest : (live.guest.data ?? null);
  const propertyName = property?.adminDisplayName ?? property?.name ?? thread.propertyName ?? null;

  return (
    <div className="bg-card border-hairline rounded-tile shadow-panel overflow-hidden border">
      {/* Ohne Reservierung UND ohne Buchung: bewusst ohne Kanal-Beispiel, die Buchung kann auch
          schlicht noch nicht angekommen sein. */}
      {!thread.bookingId && !thread.otaReservationId && (
        <p className="border-hairline bg-amber-wash text-amber-ink border-b px-4 py-3 text-[13px]">
          Keine Buchung verknüpft. Bei einer reinen Anfrage ist das normal – sonst ist die Buchung
          noch nicht eingetroffen. Buchungsdaten erscheinen, sobald sie da ist.
        </p>
      )}
      {b && <StayContextCard booking={b} propertyName={propertyName} />}
      {b && <BillingContextCard booking={b} />}
      {!demoData && live.booking.isError && (
        <p role="alert" className="border-hairline text-sealwax border-b px-4 py-3 text-[13px]">
          Buchungsdaten konnten nicht geladen werden.
        </p>
      )}
      {canViewPii && (
        <GuestContextCard
          guest={guest}
          fallbackName={threadDisplayName(thread.guestName, thread.subject)}
          state={
            demoData || !live.guestProfileId
              ? 'ready'
              : live.guest.isError
                ? 'error'
                : live.guest.isLoading
                  ? 'loading'
                  : 'ready'
          }
        />
      )}
      <ApartmentContext
        thread={thread}
        property={property}
        propertyFailed={!demoData && live.property.isError}
        onRetryProperty={() => void live.property.refetch()}
        apartments={live.apartments}
        canViewPii={canViewPii}
        demo={Boolean(demoData)}
      />
      <NoteCard threadId={thread.id} initialNote={thread.note} readOnly={!!demoData} />
      {!demoData && (
        <PanelCard icon={Bot} title="Automatik" hint="KI-Steuerung, selbst übernehmen">
          <ConversationAutomation conversationId={thread.id} embedded />
        </PanelCard>
      )}
      {!demoData && (
        <PanelCard
          icon={Puzzle}
          title="Plugin-Fälle"
          hint="Fälle, die ein Plugin zu diesem Gespräch führt"
        >
          <ConversationCases conversationId={thread.id} embedded />
        </PanelCard>
      )}
    </div>
  );
}
